import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Drawer, IconButton, Stack, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  ChevronLeftRounded as PrevIcon, ChevronRightRounded as NextIcon,
  LockOutlined as LockIcon, LockOpenOutlined as UnlockIcon, DownloadOutlined as DownloadIcon,
  PersonAddAltOutlined as AddPersonIcon, PushPinRounded as PinIcon,
  DescriptionOutlined as FileIcon, AddRounded as AddIcon, DeleteOutlineRounded as DeleteIcon,
} from '@mui/icons-material';
import PayrollService, {
  PayrollLine, PayrollRun, RunDetail, RunTotals, describeError,
} from '../../api/services/payroll.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import StatTile from '../../components/ui/StatTile';
import DataTable, { Column } from '../../components/ui/DataTable';
import ToneChip from '../../components/ui/ToneChip';
import { EmptyState, LoadingState } from '../../components/ui/States';
import { usePageBreadcrumb } from '../../components/layout/Breadcrumb';
import { downloadBlob } from '../../utils/download';
import { status as statusTokens } from '../../theme';
import PayrollPanel from './PayrollPanel';
import LineInspector from './LineInspector';
import WeekReviewCard from './WeekReviewCard';
import DocumentPreviewDialog from './DocumentPreviewDialog';
import { FileSlot } from './UploadStatementDialog';
import {
  Avatar, DayDots, Delta, RatioMath, count, money, percent, ratioPct, shortDate, weekLabel, weekdayShort,
} from './payrollUI';

// The week: one row per person, what they did and what they get.
//
// /payroll opens the newest week; /payroll/weeks/:id opens a particular
// one. Click a row and the person's week opens beside the table (a
// drawer on a narrow screen): the number, the receipt, the days, and
// the boxes for anything typed.

// The week's revenue is the statement before FedEx's deductions; the
// payroll ratio is measured against it (the server's weekRevenue).
const revenueOf = (st: PayrollRun['statement']) => st.totalCharges || st.netAmountDue;
// The same line for every company: what FedEx took off the week, or that
// it took nothing. The week's stops and packages are on Dispatches.
const statementHint = (st: PayrollRun['statement']) => (
  st.chargebacks ? `${money(st.chargebacks, { cents: true })} FedEx deductions · ${money(st.netAmountDue)} net` : 'No FedEx deductions'
);
const ratioOf = (gross: number, st: PayrollRun['statement']) => (revenueOf(st) ? Math.round((gross / revenueOf(st)) * 10000) / 100 : null);

const PayrollWeek: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('lg'));

  const [detail, setDetail] = useState<RunDetail | null>(null);
  // Last week's run, for the tiles' "vs last week" lines.
  const [previousRun, setPreviousRun] = useState<PayrollRun | null>(null);
  const previousId = detail?.previous?.runId || null;
  useEffect(() => {
    setPreviousRun(null);
    if (!previousId) return undefined;
    let live = true;
    PayrollService.run(previousId).then((d) => { if (live) setPreviousRun(d.run); }).catch(() => undefined);
    return () => { live = false; };
  }, [previousId]);
  const [noWeeks, setNoWeeks] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The week's file open in the viewer over the page, if any.
  const [viewing, setViewing] = useState<{ documentId: string; title: string } | null>(null);
  const [confirm, setConfirm] = useState<'finalize' | 'reopen' | 'delete' | null>(null);
  const [busy, setBusy] = useState(false);
  const [people, setPeople] = useState<{ accountId: string; name: string }[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  // A one-click slice of the table, from a tile or an alert.
  const [slice, setSlice] = useState<'paid' | 'no_rules' | 'skipped' | 'typed' | 'adjusted' | null>(null);
  useEffect(() => { setSlice(null); }, [runId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let id = runId;
      if (!id) {
        const runs = await PayrollService.runs();
        if (!runs.length) { setNoWeeks(true); setDetail(null); return; }
        id = runs[0]._id;
      }
      setNoWeeks(false);
      setDetail(await PayrollService.run(id));
    } catch (err) {
      setError(describeError(err, 'Could not load the week.'));
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelectedId(null); }, [runId]);

  // Only needed to assign a skipped row or add somebody; fetched lazily.
  useEffect(() => {
    if (!detail || (!addOpen && !detail.lines.some((l) => l.status === 'skipped'))) return;
    PayrollService.people().then((list) => setPeople(list.map((p) => ({ accountId: p.accountId, name: p.name })))).catch(() => undefined);
  }, [detail, addOpen]);

  const run = detail?.run || null;
  usePageBreadcrumb(run && runId ? [{ label: 'History', to: '/payroll/history' }, { label: weekLabel(run.weekStart, run.weekEnd) }] : []);

  const selected = useMemo(() => detail?.lines.find((l) => l._id === selectedId) || null, [detail, selectedId]);

  const applyLine = (line: PayrollLine, totals: RunTotals) => {
    setDetail((d) => d && ({
      ...d,
      run: { ...d.run, totals, ratio: ratioOf(totals.gross, d.run.statement) },
      lines: d.lines.map((l) => (l._id === line._id ? line : l)).filter((l) => l._id === line._id || !(line.accountId && l.accountId === line.accountId)),
    }));
  };

  // A day moved between two people comes back as both lines.
  const applyLines = (changed: PayrollLine[], totals: RunTotals) => {
    const byId = new Map(changed.map((l) => [l._id, l]));
    setDetail((d) => d && ({
      ...d,
      run: { ...d.run, totals, ratio: ratioOf(totals.gross, d.run.statement) },
      lines: d.lines.map((l) => byId.get(l._id) || l),
    }));
  };

  const dropLine = (lineId: string, totals: RunTotals) => {
    setSelectedId(null);
    setDetail((d) => d && ({
      ...d,
      run: { ...d.run, totals, ratio: ratioOf(totals.gross, d.run.statement) },
      lines: d.lines.filter((l) => l._id !== lineId),
    }));
  };

  // Somebody not on the statement. A name from the company list adds the
  // member; anything else is added as typed, for somebody outside it.
  const notOnWeek = useMemo(() => {
    const on = new Set((detail?.lines || []).map((l) => l.accountId).filter(Boolean));
    return people.filter((p) => !on.has(p.accountId));
  }, [people, detail]);
  const addPerson = async () => {
    if (!run) return;
    const name = addName.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const member = notOnWeek.find((p) => p.name.toLowerCase() === name.toLowerCase());
      const { line, totals } = await PayrollService.addLine(run._id, member ? { accountId: member.accountId } : { name });
      setDetail((d) => d && ({ ...d, run: { ...d.run, totals }, lines: [...d.lines, line] }));
      setAddOpen(false);
      setAddName('');
      setSelectedId(line._id);
    } catch (err) {
      setError(describeError(err, 'They could not be added.'));
    } finally {
      setBusy(false);
    }
  };

  // A count edited straight in the table.
  const saveCell = async (lineId: string, input: Parameters<typeof PayrollService.updateLine>[2]) => {
    if (!run) return;
    setError(null);
    try {
      const result = await PayrollService.updateLine(run._id, lineId, input);
      applyLine(result.line, result.totals);
    } catch (err) {
      setError(describeError(err, 'That change could not be saved.'));
    }
  };

  const act = async (what: 'finalize' | 'reopen' | 'delete') => {
    if (!run) return;
    setBusy(true);
    setError(null);
    try {
      if (what === 'finalize') setDetail(await PayrollService.finalize(run._id));
      if (what === 'reopen') setDetail(await PayrollService.reopen(run._id));
      if (what === 'delete') { await PayrollService.remove(run._id); navigate('/payroll/history'); }
    } catch (err) {
      setError(describeError(err, 'That could not be done.'));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  // The week's files live in Files; they open here in a viewer over the
  // page, so looking at the statement does not leave the week.
  const serviceInput = useRef<HTMLInputElement>(null);
  const openDocument = (documentId: string, title: string) => setViewing({ documentId, title });
  const [serviceOpen, setServiceOpen] = useState(false);
  const addWeeklyService = async (file: File | null) => {
    if (!run || !file) return;
    setBusy(true);
    try {
      await PayrollService.attachFiles(run._id, { weeklyService: file });
      setDetail(await PayrollService.run(run._id));
      setServiceOpen(false);
    } catch (err) {
      setError(describeError(err, 'The weekly service could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  // The week's review: check again, or undo / redo one change it made.
  const review = async (fn: () => Promise<RunDetail>, failure: string) => {
    if (!run) return;
    setBusy(true);
    try {
      setDetail(await fn());
    } catch (err) {
      setError(describeError(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const download = async (what: 'export' | 'source') => {
    if (!run) return;
    try {
      const blob = what === 'export' ? await PayrollService.exportCsv(run._id) : await PayrollService.sourceCsv(run._id);
      const end = run.weekEnd.slice(0, 10);
      downloadBlob(blob, what === 'export' ? `payroll-week-ending-${end}.csv` : run.source.fileName || `statement-${end}.csv`);
    } catch (err) {
      setError(describeError(err, 'The file could not be downloaded.'));
    }
  };

  const paysTraining = !!detail?.lines.some((l) => l.rulesSnapshot.some((r) => r.kind === 'training_day' && r.enabled !== false) || l.inputs.trainingDays > 0);

  const columns: Column<PayrollLine>[] = [
    // The statement lists drivers by FedEx ID; sorting on it puts the
    // table in the statement's order, to compare the two side by side.
    // Low to high on the first click; nobody without one sorts last.
    {
      key: 'fedexId',
      // Non-breaking, and 1% so the column shrinks to its content.
      header: 'FedEx\u00a0ID',
      hide: 'phone',
      width: '1%',
      sortValue: (l) => (l.fedexId ? (/^\d+$/.test(l.fedexId) ? Number(l.fedexId) : l.fedexId) : null),
      filter: 'text',
      render: (l) => (l.fedexId
        ? <Typography variant="body2" noWrap sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary', fontSize: 13 }}>{l.fedexId}</Typography>
        : <Typography variant="caption" color="text.disabled">—</Typography>),
    },
    {
      key: 'person',
      header: 'Driver',
      sortValue: (l) => l.name || l.statementName,
      filter: 'text',
      render: (l) => (
        <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
          <Avatar name={l.name || l.statementName} muted={l.status === 'skipped'} />
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{l.name || l.statementName}</Typography>
              {l.standingNote && (
                <Tooltip title={l.standingNote}>
                  <PinIcon aria-label="Has a note for every week" sx={{ fontSize: 14, color: 'warning.dark', flexShrink: 0 }} />
                </Tooltip>
              )}
              {(l.trainingDates?.length || 0) > 0 && (
                <Tooltip title={`In training: ${l.trainingDates!.map((d) => weekdayShort(`${d}T00:00:00.000Z`)).join(', ')}. Paid as training days, not work days.`}>
                  <span><ToneChip tone="warn" label={`${l.trainingDates!.length} training ${l.trainingDates!.length === 1 ? 'day' : 'days'}`} /></span>
                </Tooltip>
              )}
            </Stack>
            <Typography variant="caption" noWrap sx={{ display: 'block', color: l.status === 'no_rules' ? 'warning.dark' : 'text.secondary', fontWeight: l.status === 'no_rules' ? 600 : 400 }}>
              {l.custom ? 'Added by hand' : l.status === 'no_rules' ? 'No pay rules yet' : l.status === 'skipped' ? 'Skipped, nobody chosen' : l.status === 'absent' ? 'Not in statement' : ruleWords(l)}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      key: 'days',
      header: 'Days',
      hide: 'phone',
      sortValue: (l) => l.totals.days,
      filter: 'number',
      render: (l) => (
        <EditableCount
          line={l} field="daysWorked" value={l.totals.days} typed={l.totals.daysTyped} statement={l.totals.statementDays ?? l.days.length}
          empty={!l.days.length} locked={run!.status === 'finalized'} onSave={saveCell}
          shown={(
            <Stack direction="row" alignItems="center" gap={0.75}>
              {l.days.length > 0 && <DayDots weekStart={run!.weekStart} days={l.days} />}
              {l.totals.daysTyped && (
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.dark', fontVariantNumeric: 'tabular-nums' }}>{l.totals.days} typed</Typography>
              )}
            </Stack>
          )}
        />
      ),
    },
    { key: 'stops', header: 'Stops', align: 'right', numeric: true, sortValue: (l) => l.totals.stops, filter: 'number', render: (l) => <EditableCount line={l} field="stops" value={l.totals.stops} typed={l.totals.stopsTyped} statement={l.totals.statementStops} empty={!l.days.length} locked={run!.status === 'finalized'} onSave={saveCell} /> },
    { key: 'packages', header: 'Packages', align: 'right', numeric: true, hide: 'tablet', sortValue: (l) => l.totals.packages, filter: 'number', render: (l) => <EditableCount line={l} field="packages" value={l.totals.packages} typed={l.totals.packagesTyped} statement={l.totals.statementPackages} empty={!l.days.length} locked={run!.status === 'finalized'} onSave={saveCell} /> },
    // Only when somebody this week is paid for training: the statement
    // never has it, so it is typed here (double-click) or in the panel.
    ...(paysTraining ? [{
      key: 'training',
      header: 'Training',
      align: 'right' as const,
      numeric: true,
      hide: 'phone' as const,
      sortValue: (l: PayrollLine) => l.inputs.trainingDays,
      render: (l: PayrollLine) => (l.rulesSnapshot.some((r) => r.kind === 'training_day' && r.enabled !== false) || l.inputs.trainingDays > 0
        ? <EditableCount line={l} field="trainingDays" value={l.inputs.trainingDays || 0} empty={false} locked={run!.status === 'finalized'} onSave={saveCell} />
        : <Typography variant="caption" color="text.disabled">—</Typography>),
    }] : []),
    {
      key: 'pay',
      header: 'Pay',
      align: 'right',
      numeric: true,
      sortValue: (l) => (l.status === 'skipped' ? null : l.total),
      filter: 'number',
      render: (l) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 15, fontVariantNumeric: 'tabular-nums', color: l.status === 'skipped' || l.status === 'no_rules' ? 'text.disabled' : 'text.primary' }}>
            {l.status === 'skipped' ? '—' : l.status === 'no_rules' ? 'Set up' : money(l.total, { cents: true })}
          </Typography>
          {l.adjustmentsTotal !== 0 && (
            <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', color: l.adjustmentsTotal < 0 ? 'error.dark' : 'success.dark', fontWeight: 600 }}>
              {l.adjustmentsTotal > 0 ? '+' : ''}{money(l.adjustmentsTotal)} {l.adjustments.length === 1 ? adjWord(l) : 'adjustments'}
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  if (loading && !detail) {
    return (
      <PageBody>
        <PayrollPanel onUploaded={() => load()} />
        <LoadingState />
      </PageBody>
    );
  }

  if (noWeeks) {
    return (
      <PageBody>
        <PayrollPanel onUploaded={() => load()} />
        <PageHeader title="This week" />
        <EmptyState
          title="No weeks yet"
          description="Upload the weekly statement to calculate pay."
        />
      </PageBody>
    );
  }

  const noRules = detail?.lines.filter((l) => l.status === 'no_rules') || [];
  const skipped = detail?.lines.filter((l) => l.status === 'skipped') || [];
  const typed = detail?.lines.filter((l) => l.totals.daysTyped || l.totals.stopsTyped || l.totals.packagesTyped) || [];
  const adjusted = detail?.lines.filter((l) => l.adjustments.length > 0) || [];
  const SLICE_LABEL = { paid: 'people paid', no_rules: 'no pay rules', skipped: 'skipped rows', typed: 'typed over the statement', adjusted: 'with adjustments' } as const;
  const sliced = !detail ? [] : !slice ? detail.lines : detail.lines.filter((l) => (
    slice === 'paid' ? (l.status === 'ok' || l.status === 'absent')
      : slice === 'no_rules' ? l.status === 'no_rules'
        : slice === 'skipped' ? l.status === 'skipped'
          : slice === 'typed' ? (l.totals.daysTyped || l.totals.stopsTyped || l.totals.packagesTyped)
            : l.adjustments.length > 0
  ));
  const toggleSlice = (s: typeof slice) => setSlice((cur) => (cur === s ? null : s));
  // Against last week, measured the same way as this week's tiles.
  const was = previousRun && run ? {
    gross: previousRun.totals.gross,
    revenue: revenueOf(previousRun.statement),
    ratio: ratioPct(previousRun.totals.gross, revenueOf(previousRun.statement)),
    perDispatch: previousRun.totals.dispatches ? revenueOf(previousRun.statement) / previousRun.totals.dispatches : null,
  } : null;
  const ratioNow = run ? ratioPct(run.totals.gross, revenueOf(run.statement)) : null;
  const ratioDelta = was && ratioNow !== null && was.ratio !== null ? ratioNow - was.ratio : null;
  const perDispatchNow = run && run.totals.dispatches ? revenueOf(run.statement) / run.totals.dispatches : null;

  const inspector = run && selected && (
    <LineInspector run={run} line={selected} people={people} weekLines={detail?.lines || []} onChange={applyLine} onMoved={applyLines} onRemoved={dropLine} onClose={() => setSelectedId(null)} />
  );

  return (
    <PageBody fluid>
      <PayrollPanel onUploaded={() => load()} latestWeek={detail ? !detail.next : undefined} />
      {run && (
        <>
          <PageHeader
            title={(
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <Tooltip title={detail?.previous ? `Week ending ${shortDate(detail.previous.weekEnd)}` : 'No earlier week'}>
                  <span>
                    <IconButton size="small" aria-label="Previous week" disabled={!detail?.previous} onClick={() => navigate(`/payroll/weeks/${detail!.previous!.runId}`)}><PrevIcon /></IconButton>
                  </span>
                </Tooltip>
                <span>{weekLabel(run.weekStart, run.weekEnd)}</span>
                <Tooltip title={detail?.next ? `Week ending ${shortDate(detail.next.weekEnd)}` : 'This is the newest week'}>
                  <span>
                    <IconButton size="small" aria-label="Next week" disabled={!detail?.next} onClick={() => navigate(`/payroll/weeks/${detail!.next!.runId}`)}><NextIcon /></IconButton>
                  </span>
                </Tooltip>
                <ToneChip tone={run.status === 'finalized' ? 'ok' : 'warn'} label={run.status === 'finalized' ? 'Finalized' : 'Draft'} />
              </Stack>
            )}
            subtitle={`Uploaded ${shortDate(run.source.uploadedAt)}${run.finalizedAt ? `, finalized ${shortDate(run.finalizedAt)}` : ''}`}
            actions={(
              <>
                {run.status === 'draft' ? (
                  <Button variant="contained" disableElevation startIcon={<LockIcon />} onClick={() => setConfirm('finalize')} disabled={busy}>Finalize week</Button>
                ) : (
                  <Button variant="outlined" startIcon={<UnlockIcon />} onClick={() => setConfirm('reopen')} disabled={busy}>Reopen</Button>
                )}
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => download('export')}>Export CSV</Button>
                <input
                  ref={serviceInput} type="file" hidden accept=".pdf,.csv,.xlsx,.xls,.jpg,.jpeg,.png,.heic"
                  data-testid="add-weekly-service"
                  onChange={(e) => { addWeeklyService(e.target.files?.[0] || null); e.target.value = ''; }}
                />
                <Dialog open={serviceOpen} onClose={busy ? undefined : () => setServiceOpen(false)} maxWidth="sm" fullWidth>
                  <DialogTitle>Add the weekly service</DialogTitle>
                  <DialogContent>
                    <FileSlot
                      main
                      optional={false}
                      label="Weekly service"
                      help="FedEx's weekly service report for this week. Not used for pay; kept with the statement under Payroll in Files."
                      note={busy ? 'Saving…' : undefined}
                      disabled={busy}
                      onChoose={() => serviceInput.current?.click()}
                      onDropFiles={(list) => addWeeklyService(list[0] || null)}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setServiceOpen(false)} disabled={busy}>Cancel</Button>
                  </DialogActions>
                </Dialog>
                <DocumentPreviewDialog documentId={viewing?.documentId || null} title={viewing?.title || ''} onClose={() => setViewing(null)} />
              </>
            )}
          />

          {/* The week's files and the rarer actions: always in view, kept quiet. */}
          <Stack
            direction="row" alignItems="center" flexWrap="wrap" gap={0.5}
            sx={{ mt: -1, mb: 2, '& .MuiButton-root': { color: 'text.secondary', fontWeight: 500, textTransform: 'none', px: 1, minWidth: 0, '&:hover': { color: 'text.primary' } } }}
          >
            {run.files?.statementDocumentId && (
              <Button size="small" startIcon={<FileIcon fontSize="small" />} onClick={() => openDocument(run.files!.statementDocumentId!, 'Statement')}>View statement</Button>
            )}
            {run.files?.weeklyServiceDocumentId ? (
              <Button size="small" startIcon={<FileIcon fontSize="small" />} onClick={() => openDocument(run.files!.weeklyServiceDocumentId!, 'Weekly service')}>View weekly service</Button>
            ) : (
              <Button size="small" startIcon={<AddIcon fontSize="small" />} disabled={busy} onClick={() => setServiceOpen(true)}>Add weekly service</Button>
            )}
            <Button size="small" startIcon={<DownloadIcon fontSize="small" />} onClick={() => download('source')}>Download original statement</Button>
            {run.status === 'draft' && (
              <Button size="small" startIcon={<DeleteIcon fontSize="small" />} onClick={() => setConfirm('delete')} sx={{ '&:hover': { color: 'error.main !important' } }}>Delete draft</Button>
            )}
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
            <Box component="button" type="button" aria-pressed={slice === 'paid'} aria-label="Only people paid" onClick={() => toggleSlice('paid')} sx={{ display: 'block', width: '100%', height: '100%', p: 0, border: 0, bgcolor: 'transparent', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', borderRadius: 3, outline: slice === 'paid' ? '2px solid' : 'none', outlineColor: 'primary.main' }}>
              <StatTile
                label="Gross payroll"
                value={money(run.totals.gross)}
                hint={`${run.totals.paid} ${run.totals.paid === 1 ? 'person' : 'people'} paid`}
                trend={was && <Delta diff={run.totals.gross - was.gross} better="down" format={(n) => money(Math.round(n))} />}
              />
            </Box>
            {run.statement.chargebacks ? (
              <Tooltip
                placement="bottom"
                title={(
                  <Box sx={{ py: 0.25 }}>
                    {(run.statement.deductions || []).map((d) => (
                      <Stack key={`${d.type}${d.description}`} direction="row" justifyContent="space-between" gap={2}>
                        <span>{d.type}{d.description ? ` (${d.description})` : ''}</span>
                        <span>{money(d.amount, { cents: true })}</span>
                      </Stack>
                    ))}
                    <Stack direction="row" justifyContent="space-between" gap={2} sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider', fontWeight: 600 }}>
                      <span>Paid out</span><span>{money(run.statement.netAmountDue, { cents: true })}</span>
                    </Stack>
                  </Box>
                )}
              >
                <Box sx={{ height: '100%' }}>
                  <StatTile label="Statement" value={money(revenueOf(run.statement))} hint={statementHint(run.statement)} trend={was && <Delta diff={revenueOf(run.statement) - was.revenue} better="up" format={(n) => money(Math.round(n))} />} />
                </Box>
              </Tooltip>
            ) : (
              <StatTile label="Statement" value={money(revenueOf(run.statement))} hint={statementHint(run.statement)} trend={was && <Delta diff={revenueOf(run.statement) - was.revenue} better="up" format={(n) => money(Math.round(n))} />} />
            )}
            <RatioMath rows={[{ label: 'Payroll', amount: run.totals.gross, over: 'statement before deductions', base: revenueOf(run.statement) }]}>
              <StatTile
                label="Payroll ratio"
                value={percent(ratioNow)}
                hint="of the statement before deductions"
                trend={<Delta diff={ratioDelta} better="down" format={(n) => `${n.toFixed(2)} pts`} />}
              />
            </RatioMath>
            <StatTile
              label="Dispatches"
              value={count(run.totals.dispatches)}
              hint={(
                <>
                  {count(run.statement.totalStops)} stops, {count(run.statement.totalPackages)} packages
                  {perDispatchNow !== null && <><br />{money(Math.round(perDispatchNow))} revenue each</>}
                </>
              )}
              trend={perDispatchNow !== null && was?.perDispatch != null && (
                <Delta diff={perDispatchNow - was.perDispatch} better="up" format={(n) => `${money(Math.round(n))} each`} />
              )}
            />
          </Box>

          <WeekReviewCard
            review={run.review}
            locked={run.status === 'finalized'}
            busy={busy}
            onRecheck={() => review(() => PayrollService.reviewRun(run._id), 'The week could not be checked.')}
            onUndo={(key, undo) => review(() => PayrollService.setReviewItem(run._id, key, undo), 'That was not changed.')}
            onAddWorksheet={() => setServiceOpen(true)}
          />

          {!run.statement.reconciled && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Driver rows add up to {count(run.statement.driverStopsTotal)} stops; the statement says {count(run.statement.totalStops)}.
            </Alert>
          )}
          {noRules.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2, cursor: 'pointer' }} onClick={() => toggleSlice('no_rules')} role="button" aria-pressed={slice === 'no_rules'} action={<Button color="inherit" size="small" component={RouterLink} to={`/payroll/people/${noRules[0].accountId}`} onClick={(e: React.MouseEvent) => e.stopPropagation()}>Set up pay</Button>}>
              <strong>{noRules.map((l) => l.name).join(', ')}</strong>: no pay rules yet.
            </Alert>
          )}
          {skipped.length > 0 && (
            <Alert severity="info" sx={{ mb: 2, cursor: 'pointer' }} onClick={() => toggleSlice('skipped')} role="button" aria-pressed={slice === 'skipped'}>
              {skipped.length} {skipped.length === 1 ? 'row' : 'rows'} skipped, nobody chosen.
            </Alert>
          )}
          {(typed.length > 0 || adjusted.length > 0 || slice) && (
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
              {typed.length > 0 && <Chip size="small" label={`${typed.length} typed over the statement`} variant={slice === 'typed' ? 'filled' : 'outlined'} onClick={() => toggleSlice('typed')} />}
              {adjusted.length > 0 && <Chip size="small" label={`${adjusted.length} with adjustments`} variant={slice === 'adjusted' ? 'filled' : 'outlined'} onClick={() => toggleSlice('adjusted')} />}
              {slice && <Chip size="small" label={`Showing ${SLICE_LABEL[slice]}`} onDelete={() => setSlice(null)} color="primary" />}
            </Stack>
          )}

          <Stack direction="row" gap={2.5} alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <DataTable
                twoLine
                columns={columns}
                // The statement's own order, so the two read side by side.
                defaultSort={{ key: 'fedexId', dir: 'asc' }}
                rows={sliced}
                rowKey={(l) => l._id}
                onRowClick={(l) => setSelectedId(l._id)}
                rowAccent={(l) => (l._id === selectedId ? statusTokens.info : l.status === 'no_rules' ? statusTokens.warn : undefined)}
                empty={<EmptyState title="Nobody on this week" description="The statement had no driver rows and nobody has pay rules." compact />}
                footer={run.status === 'draft' ? (
                  addOpen ? (
                    <Stack direction="row" alignItems="center" gap={1} onClick={(e) => e.stopPropagation()}>
                      <Autocomplete
                        freeSolo
                        size="small"
                        options={notOnWeek.map((p) => p.name)}
                        inputValue={addName}
                        onInputChange={(_, v) => setAddName(v)}
                        sx={{ flex: 1, maxWidth: 360 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            autoFocus
                            placeholder="Name"
                            inputProps={{ ...params.inputProps, 'aria-label': 'Name' }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && addName.trim()) { e.preventDefault(); addPerson(); }
                              if (e.key === 'Escape') { setAddOpen(false); setAddName(''); }
                            }}
                          />
                        )}
                      />
                      <Button size="small" variant="contained" disableElevation onClick={addPerson} disabled={busy || !addName.trim()}>Add</Button>
                      <Button size="small" onClick={() => { setAddOpen(false); setAddName(''); }} disabled={busy}>Cancel</Button>
                    </Stack>
                  ) : (
                    <Button size="small" startIcon={<AddPersonIcon />} onClick={() => setAddOpen(true)} sx={{ color: 'text.secondary', px: 1 }}>
                      Add a person or expense
                    </Button>
                  )
                ) : undefined}
              />
            </Box>
            {wide && inspector && (
              <Box sx={{ width: 400, flexShrink: 0, position: 'sticky', top: 16 }}>{inspector}</Box>
            )}
          </Stack>

          {!wide && (
            <Drawer anchor="right" open={!!selected} onClose={() => setSelectedId(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 1.5, bgcolor: 'background.default' } }}>
              {inspector}
            </Drawer>
          )}

          <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)}>
            <DialogTitle>
              {confirm === 'finalize' && 'Finalize this week?'}
              {confirm === 'reopen' && 'Reopen this week?'}
              {confirm === 'delete' && 'Delete this draft?'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {confirm === 'finalize' && `${money(run.totals.gross)} for ${run.totals.paid} people. Nothing changes until it is reopened.`}
                {confirm === 'reopen' && 'The week goes back to draft.'}
                {confirm === 'delete' && 'The week and everything typed on it are removed.'}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
              <Button variant="contained" disableElevation color={confirm === 'delete' ? 'error' : 'primary'} onClick={() => act(confirm!)} disabled={busy}>
                {confirm === 'finalize' && 'Finalize'}
                {confirm === 'reopen' && 'Reopen'}
                {confirm === 'delete' && 'Delete draft'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </PageBody>
  );
};

// A count in the table. Double-click and it is a field where it stands;
// Enter or leaving saves, Escape puts it back. Marked when it was typed
// over the statement.
const EditableCount: React.FC<{
  line: PayrollLine;
  field: 'daysWorked' | 'stops' | 'packages' | 'trainingDays';
  value: number;
  shown?: React.ReactNode;
  typed?: boolean;
  statement?: number;
  empty: boolean;
  locked: boolean;
  onSave: (lineId: string, input: Parameters<typeof PayrollService.updateLine>[2]) => Promise<void>;
}> = ({ line, field, value, shown, typed, statement, empty, locked, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const label = `${{ daysWorked: 'Days', stops: 'Stops', packages: 'Packages', trainingDays: 'Training days' }[field]} for ${line.name || line.statementName}`;
  const start = (e: React.MouseEvent) => {
    if (locked || line.status === 'skipped') return;
    e.stopPropagation();
    setDraft(String(value));
    setEditing(true);
  };
  const finish = async (save: boolean) => {
    setEditing(false);
    if (!save) return;
    const next = draft.trim() === '' ? null : Number(draft);
    if (next !== null && !Number.isFinite(next)) return;
    if (next === value) return;
    await onSave(line._id, { inputs: { [field]: next } });
  };
  if (editing) {
    return (
      <Box
        component="input"
        autoFocus
        aria-label={label}
        inputMode="decimal"
        value={draft}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
        onBlur={() => finish(true)}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') { e.preventDefault(); finish(true); }
          if (e.key === 'Escape') finish(false);
        }}
        sx={{
          width: 72, textAlign: 'right', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
          p: 0, border: 0, outline: 0, bgcolor: 'transparent', borderBottom: '2px solid', borderColor: 'primary.main', borderRadius: 0,
        }}
      />
    );
  }
  const body = empty && !typed ? <Typography variant="caption" color="text.disabled">—</Typography> : shown ?? (
    <Typography component="span" variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', color: typed ? 'primary.dark' : 'text.primary', fontWeight: typed ? 600 : 400 }}>
      {count(value)}
    </Typography>
  );
  const title = shown ? '' : typed ? `Typed in. The statement shows ${count(statement || 0)}. Double-click to change.` : locked || line.status === 'skipped' ? '' : 'Double-click to change';
  const span = <Box component="span" onDoubleClick={start} sx={{ cursor: locked ? 'default' : 'text', display: 'inline-block', minWidth: 24 }}>{body}</Box>;
  // A cell that brings its own display (the day dots) has its own tooltip.
  return title ? <Tooltip title={title} placement="top" enterDelay={600}>{span}</Tooltip> : span;
};

/** "Day rate + over threshold", from the rules the week was paid under. */
function ruleWords(l: PayrollLine): string {
  const names: Record<string, string> = {
    day_rate: 'Day rate', per_stop: 'Per stop', over_threshold: 'over threshold', hourly: 'Hourly',
    weekly_salary: 'Weekly salary', training_day: 'training', vacation_day: 'vacation', bonus: 'bonus', deduction: 'deduction',
  };
  const parts = l.rulesSnapshot.filter((r) => r.enabled !== false && r.amount != null).map((r) => names[r.kind] || r.kind);
  return parts.length ? parts.join(' + ') : '';
}

function adjWord(l: PayrollLine): string {
  const a = l.adjustments[0];
  if (!a) return '';
  return a.note ? a.note.toLowerCase().slice(0, 18) : a.kind;
}

export default PayrollWeek;
